import DocumentationButton from "components/DocumentationButton";
import { EmptystateCloudColored } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import Feedback from "ds/components/Feedback";
import PageLoading from "components/loading/PageLoading";
import Tooltip from "ds/components/Tooltip";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import { Action, IntentProject } from "types/generated";
import useAttachableAwsIntegrations from "shared/IntentProject/useAttachableAwsIntegrations";
import { getDocsUrl } from "utils/getDocsUrl";
import IntentProjectAwsIntegrationDetails from "components/AttachCloud/IntentProjectDetails";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useIntentProjectIntegration from "shared/IntentProject/useIntentProjectIntegration";
import { checkAllowedAction } from "utils/access";

import { showAttachIntegrationDrawer } from "./AttachDrawer";

type IntentProjectIntegrationsProps = {
  intentProject: IntentProject;
};

const IntentProjectIntegrations = ({ intentProject }: IntentProjectIntegrationsProps) => {
  useTitle(`Integrations · ${intentProject.name}`);

  const {
    awsIntegrationAttachment,
    loading: integrationLoading,
    error: integrationError,
  } = useIntentProjectIntegration(intentProject.id);
  const { attachableAwsIntegrations, initialLoading, error, hasData } =
    useAttachableAwsIntegrations(intentProject.space.id);

  const ErrorContent = useErrorHandle(integrationError || error);

  const hasAlreadyAttachedIntegration = !!awsIntegrationAttachment;
  const hasAttachableAwsIntegrations = hasData && attachableAwsIntegrations.length > 0;
  const canAttach = !hasAlreadyAttachedIntegration && hasAttachableAwsIntegrations;

  const canAttachIntegration = checkAllowedAction(
    intentProject,
    Action.IntentProjectCloudIntegrationAttach
  );
  const canDetachIntegration = checkAllowedAction(
    intentProject,
    Action.IntentProjectCloudIntegrationDetach
  );

  const attachment = awsIntegrationAttachment;

  const handleAttachIntegrationDrawer = () => {
    showAttachIntegrationDrawer({
      intentProjectId: intentProject.id,
      attachableAwsIntegrations,
    });
  };

  if (ErrorContent) {
    return ErrorContent;
  }

  if (integrationLoading || initialLoading) {
    return <PageLoading />;
  }

  return (
    <>
      <PageInfoHeader title="Integrations">
        {canAttach && (
          <Tooltip
            on={(props) => (
              <Button
                {...props}
                variant="primary"
                onPress={handleAttachIntegrationDrawer}
                disabled={!canAttachIntegration.result}
              >
                Attach integration
              </Button>
            )}
            active={!!canAttachIntegration.message}
          >
            {canAttachIntegration.message}
          </Tooltip>
        )}
      </PageInfoHeader>

      <Box direction="column" gap="x-large" fullHeight>
        <Feedback type="callout" variant="info">
          You can only attach AWS integrations from the current space and parent spaces that you
          inherit from.
        </Feedback>

        {/* Empty state - No attachable integrations */}
        {!hasAttachableAwsIntegrations && !hasAlreadyAttachedIntegration && (
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateCloudColored}
              title="You do not have any AWS integrations yet"
              caption="AWS Integrations are used to dynamically generate short-lived credentials to authenticate to AWS. In this way you avoid using static credentials and by doing so, you reduce breaches."
            >
              <DocumentationButton
                to={getDocsUrl("/integrations/cloud-providers")}
                label="Documentation"
              />
            </EmptyState>
          </Box>
        )}

        {/* Empty state - No attached integrations but has attachable ones */}
        {hasAttachableAwsIntegrations && !hasAlreadyAttachedIntegration && (
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateCloudColored}
              title="You do not have any attached AWS integrations yet"
              caption="You can attach one of the existing AWS integrations to this Intent Project."
            >
              <Box gap="medium">
                {canAttach && (
                  <Tooltip
                    on={(props) => (
                      <Button
                        {...props}
                        variant="primary"
                        onPress={handleAttachIntegrationDrawer}
                        disabled={!canAttachIntegration.result}
                      >
                        Attach AWS integration
                      </Button>
                    )}
                    active={!!canAttachIntegration.message}
                  >
                    {canAttachIntegration.message}
                  </Tooltip>
                )}
                <DocumentationButton
                  to={getDocsUrl("/integrations/cloud-providers")}
                  label="Documentation"
                />
              </Box>
            </EmptyState>
          </Box>
        )}

        {/* Attached integrations list */}
        {attachment && (
          <Box direction="column" padding="0 x-large" gap="large" limitWidth="medium">
            <IntentProjectAwsIntegrationDetails
              key={attachment.id}
              attachment={attachment}
              intentProjectId={intentProject.id}
              readOnly={!canDetachIntegration.result}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default IntentProjectIntegrations;
